import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { membershipApi, type Membership } from '../../api/memberships';

export function MembershipManagementPage() {
  const [items, setItems] = useState<Membership[]>([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');

  async function load() {
    setLoading(true);
    try {
      setItems(await membershipApi.list(status ? { status } : undefined));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load memberships');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [status]);

  async function action(id: string, fn: (id: string) => Promise<unknown>) {
    setBusyId(id);
    try {
      await fn(id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusyId('');
    }
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="flex flex-wrap justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Membership Management</h1>
          <p className="mt-1 text-sm text-slate-600">Activate, suspend, restore, and archive memberships.</p>
        </div>
        <Link to="/members" className="text-sm text-slate-600 hover:underline">← Directory</Link>
      </div>
      <div className="mt-6">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border px-3 py-2 text-sm">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="archived">Archived</option>
          <option value="pending">Pending</option>
        </select>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {loading ? <p className="mt-6">Loading…</p> : (
        <ul className="mt-6 space-y-3">
          {items.map((m) => (
            <li key={m.id} className="rounded-xl border bg-white p-4 text-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium">{m.identity_id}</p>
                  <p className="text-slate-500 capitalize">{m.scope.type} · {m.status}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {m.status !== 'active' && (
                    <button disabled={busyId === m.id} onClick={() => void action(m.id, membershipApi.activate)} className="rounded border px-2 py-1">Activate</button>
                  )}
                  {m.status === 'active' && (
                    <button disabled={busyId === m.id} onClick={() => void action(m.id, membershipApi.suspend)} className="rounded border px-2 py-1">Suspend</button>
                  )}
                  {(m.status === 'suspended' || m.status === 'archived') && (
                    <button disabled={busyId === m.id} onClick={() => void action(m.id, membershipApi.restore)} className="rounded border px-2 py-1">Restore</button>
                  )}
                  {m.status === 'active' && (
                    <button disabled={busyId === m.id} onClick={() => void action(m.id, membershipApi.archive)} className="rounded border px-2 py-1">Archive</button>
                  )}
                  <Link to={`/members/${m.id}`} className="rounded border px-2 py-1 text-slate-700">Details</Link>
                </div>
              </div>
            </li>
          ))}
          {items.length === 0 && <p className="text-slate-500">No memberships found.</p>}
        </ul>
      )}
    </div>
  );
}
